import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { toast } from 'sonner';
import type {
  BulkProgressLinkResult, MonthlyReviewDraft, MonthlyReviewDto, MonthlyReviewStatus, MonthlyReviewSummaryRow, MonthlyReviewSweep,
  ProgressLink, ProgressLinkSummary, ProgressTimeline, SessionNotesPayload,
} from '@repo/shared-types';
import type { PutSessionNotesInput } from '@repo/validation';
import { fetchApi } from '../lib/api';
import { useSubscription } from './useSubscription';

// THI-105 — one module for every progress-tracking hook (session notes now,
// monthly reviews / progress link / timeline appended by later tasks). Keep
// it one file per the File Structure table; do not fork a second module.

export function useSessionNotes(sessionId: string | null) {
  return useQuery<SessionNotesPayload>({
    queryKey: ['session-notes', sessionId],
    queryFn: () => fetchApi(`/sessions/${sessionId}/notes`),
    enabled: sessionId !== null,
  });
}

export function useSaveSessionNotes(sessionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: PutSessionNotesInput) =>
      fetchApi<SessionNotesPayload>(`/sessions/${sessionId}/notes`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    onSuccess: (data) => {
      qc.setQueryData(['session-notes', sessionId], data);
      qc.invalidateQueries({ queryKey: ['sessions', sessionId] });
    },
    onError: (e) => toast.error(e.message || 'Không lưu được nhận xét'),
  });
}

// ── Monthly reviews (THI-105) ────────────────────────────────────────────

// THI-189: the sweep is SUBJECT-scoped everywhere a teacher sees it — a
// student in two slots of one subject shares one review, and naming a slot
// on this query key would just invite the two call sites to disagree.
export function useSubjectMonthlyReviewSweep(subjectId: string | null, month: string) {
  return useQuery<MonthlyReviewSweep>({
    queryKey: ['monthly-reviews', subjectId, month],
    queryFn: () => fetchApi(`/subjects/${subjectId}/monthly-reviews?month=${month}`),
    enabled: subjectId !== null,
  });
}

/** One row per subject with an active roster this month — feeds the
 *  "Nhận xét tháng" index page and the workbench nudge from ONE call, so the
 *  two can never disagree (spec §10.2/§10.3). */
export function useMonthlyReviewSummary(month: string) {
  return useQuery<MonthlyReviewSummaryRow[]>({
    queryKey: ['monthly-reviews-summary', month],
    queryFn: () => fetchApi(`/monthly-reviews/summary?month=${month}`),
  });
}

/** One month, one subject, for one student — the notes the editor shows beside
 *  the writing box. Same endpoint the Tiến độ tab uses, narrowed. */
export function useStudentMonthNotes(studentId: string | null, subjectId: string, month: string) {
  return useQuery<ProgressTimeline>({
    queryKey: ['progress-timeline', studentId, month, subjectId],
    queryFn: () => fetchApi(`/students/${studentId}/progress-timeline?month=${month}&subjectId=${subjectId}`),
    enabled: studentId !== null,
  });
}

export function useSaveMonthlyReview(studentId: string, subjectId: string, month: string) {
  const qc = useQueryClient();
  return useMutation({
    // THI-348: `status` absent = PUBLISHED (the API's default), so every
    // existing caller keeps publishing; only a draft's "Lưu nháp" sends DRAFT.
    mutationFn: (data: { content: string; aiDrafted: boolean; status?: MonthlyReviewStatus }) =>
      fetchApi<MonthlyReviewDto>(`/students/${studentId}/monthly-reviews/${subjectId}/${month}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['monthly-reviews'] });
      qc.invalidateQueries({ queryKey: ['monthly-reviews-summary'] });
      qc.invalidateQueries({ queryKey: ['progress-timeline', studentId] });
    },
    onError: (e) => toast.error(e.message || 'Không lưu được nhận xét'),
  });
}

/** THI-348: "Duyệt cả n nháp" — publishes every draft of the subject's roster
 *  for the month in one call. */
export function usePublishReviewDrafts(subjectId: string, month: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      fetchApi<{ published: number }>(`/subjects/${subjectId}/monthly-reviews/publish-drafts?month=${month}`, { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['monthly-reviews'] });
      qc.invalidateQueries({ queryKey: ['monthly-reviews-summary'] });
      qc.invalidateQueries({ queryKey: ['progress-timeline'] });
    },
    onError: (e) => toast.error(e.message || 'Không duyệt được nháp'),
  });
}

/** Returns a DRAFT; it does not save. The save goes through
 *  useSaveMonthlyReview with `aiDrafted: true`. `fetchApi` throws the error
 *  body's `code` on the thrown Error (see lib/api.ts), so the caller tells
 *  NO_NOTES / TIER_LIMIT_REACHED / AI_DRAFT_FAILED apart by `e.code`, never by
 *  matching the Vietnamese message text. */
export function useAiDraft(studentId: string, subjectId: string, month: string) {
  return useMutation({
    mutationFn: () =>
      fetchApi<MonthlyReviewDraft>(
        `/students/${studentId}/monthly-reviews/${subjectId}/${month}/ai-draft`,
        { method: 'POST' },
      ),
  });
}

// ── Progress timeline (THI-105, Task 12) ──────────────────────────────────

/** The Tiến độ tab's dòng thời gian — the API groups by month and returns
 *  aggregates already (Task 4); this hook does not re-group on the client. */
export function useProgressTimeline(studentId: string | null, months: number) {
  return useQuery<ProgressTimeline>({
    queryKey: ['progress-timeline', studentId, months],
    queryFn: () => fetchApi(`/students/${studentId}/progress-timeline?months=${months}`),
    enabled: studentId !== null,
    // "Xem tháng … và trước đó" re-keys on `months`; without this the query
    // goes pending and the timeline flashes to the empty state instead of
    // keeping the current months on screen until the wider window resolves
    // (same reason useInvoicingPreview carries it — hooks/useInvoicing.ts:23).
    placeholderData: keepPreviousData,
  });
}

// ── Sổ liên lạc link (THI-105, Task 13) ───────────────────────────────────

/**
 * `data: null` means "no token yet" — a normal, common state, not an error.
 * A FREE org never even fires this request: `Plan.allowsProgressLink`
 * (shared-types/pricing.ts) is checked client-side first, same as
 * `useExtrasAccess`/bookkeeping/payroll gate their own GETs — without that,
 * a FREE org's teacher opening the Tiến độ tab would 403 on page load and
 * trip `fetchApi`'s shared upgrade-dialog side effect
 * (`maybeOpenUpgradeDialog`, lib/api.ts) on a passive read, popping the
 * modal over the inline upsell box the design actually wants there.
 * `retry: false` stays anyway as a backstop for the rare case the cached
 * subscription is stale and the server 403s regardless — ProgressLinkBlock
 * treats that residual FEATURE_LOCKED the same as the plan flag being false.
 */
export function useProgressLink(studentId: string | null) {
  const { data: subscription } = useSubscription();
  return useQuery<ProgressLink | null>({
    queryKey: ['progress-link', studentId],
    queryFn: () => fetchApi(`/students/${studentId}/progress-link`),
    enabled: studentId !== null && subscription?.plan.allowsProgressLink === true,
    retry: false,
  });
}

export function useCreateProgressLink(studentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (regenerate: boolean) =>
      fetchApi<ProgressLink>(`/students/${studentId}/progress-link`, {
        method: 'POST',
        body: JSON.stringify({ regenerate }),
      }),
    onSuccess: (data) => qc.setQueryData(['progress-link', studentId], data),
    onError: (e) => toast.error(e.message || 'Không tạo được link'),
  });
}

/** For the student-list confirm dialog's "Sẽ tạo link cho {{n}} em chưa có,
 *  {{m}} em đã có giữ nguyên." Same plan-gate rule as `useProgressLink`: only
 *  fires once the subscription is known to allow it, so a FREE org's teacher
 *  never trips a passive 403 just by opening the dropdown. */
export function useProgressLinkSummary() {
  const { data: subscription } = useSubscription();
  return useQuery<ProgressLinkSummary>({
    queryKey: ['progress-links-summary'],
    queryFn: () => fetchApi('/students/progress-links/summary'),
    enabled: subscription?.plan.allowsProgressLink === true,
    retry: false,
  });
}

export function useBulkCreateProgressLinks() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (studentIds?: string[]) =>
      fetchApi<BulkProgressLinkResult>('/students/progress-links/bulk', {
        method: 'POST',
        body: JSON.stringify({ studentIds }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['progress-links-summary'] });
      qc.invalidateQueries({ queryKey: ['progress-link'] });
      // A student's row on the invoice review card reads `hasProgressLink`
      // off the invoice detail payload — invalidate it too so the "Tạo ngay"
      // prompt disappears the moment its students all have links.
      qc.invalidateQueries({ queryKey: ['invoices'] });
    },
    onError: (e) => toast.error(e.message || 'Không tạo được link'),
  });
}
